// no default React import needed with react-jsx
import {
  Grid,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material'
import {
  People as PeopleIcon,
  Route as RouteIcon,
  Place as PlaceIcon,
  Storage as StorageIcon,
  Queue as QueueIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import MetricsCard from '../components/MetricsCard'
import ActivityFeed from '../components/ActivityFeed'
import { useRealTimeData } from '../hooks/useRealTimeData'

export default function OverviewPage() {
  const { metrics, activityFeed, loading, error, refresh } = useRealTimeData()

  const formatBytes = (bytes?: number) => {
    if (bytes === undefined || bytes === null) return 'N/A'
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatErrorRate = (rate?: number) => {
    if (rate === undefined || rate === null) return 'N/A'
    return `${(rate * 100).toFixed(2)}%`
  }

  if (loading && !metrics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard Overview
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Active Users (24h)"
            value={metrics?.active_users_24h ?? 'N/A'}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Tracks"
            value={metrics?.total_tracks ?? 'N/A'}
            subtitle={metrics?.tracks_today !== undefined ? `${metrics.tracks_today} created today` : 'N/A'}
            icon={<RouteIcon />}
            color="success"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Waypoints"
            value={metrics?.total_waypoints ?? 'N/A'}
            subtitle={metrics?.waypoints_today !== undefined ? `${metrics.waypoints_today} created today` : 'N/A'}
            icon={<PlaceIcon />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Storage Usage"
            value={formatBytes(metrics?.storage_used_bytes)}
            icon={<StorageIcon />}
            color="warning"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Processing Queue"
            value={metrics?.processing_queue_count ?? 'N/A'}
            subtitle="Pending waypoints"
            icon={<QueueIcon />}
            color="secondary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Error Rate (24h)"
            value={formatErrorRate(metrics?.error_rate_24h)}
            icon={<ErrorIcon />}
            color={metrics?.error_rate_24h && metrics.error_rate_24h > 0.05 ? 'error' : 'success'}
          />
        </Grid>
      </Grid>

      {/* Activity Feed */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <ActivityFeed activities={activityFeed} loading={loading} />
        </Grid>
        
        <Grid item xs={12} lg={4}>
          {/* System Status */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            
            <Button variant="outlined" fullWidth>
              View Processing Queue
            </Button>
            
            <Button variant="outlined" fullWidth>
              Moderation Queue
            </Button>
            
            <Button variant="outlined" fullWidth>
              System Health
            </Button>
            
            {metrics?.last_updated && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2 }}>
                Last updated: {new Date(metrics.last_updated).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}